import { Header } from '@/components/Header/Header';
import { BottomBar } from '@/components/BottomBar/BottomBar';
import { VideoButton } from '@/components/VideoButton';
import { SPECIALIZATION_LABELS_RU, type City } from '@pomoshnik/shared';
import type { AuthProfile, AuthSubscription } from '@/lib/stores/authStore';
import { CABINET_VIDEO_URL } from './CABINET_VIDEO_URL';
import { getInitials, getSubscriptionPresentation } from './cabinetSubscription';

/**
 * Presenter Экрана 2.11.1 «Личный кабинет» — Block 11 Phase 11.A (ADR-0005).
 *
 * Чистая функция props → JSX. Никаких useEffect / fetch / useNavigate —
 * всё в Container `CabinetHub.tsx`.
 *
 * **Layout (P-046 shell):** `<main bg-canvas><Header/><section overflow-y-auto>
 * …</section><BottomBar variant="default"/></main>`.
 *
 * **Порядок контента (P-040 VideoButton ПЕРВЫМ):**
 *   1. `<VideoButton>` (видеоинструкция).
 *   2. Карточка профиля (`bg-card`, аватар-инициалы, кнопка «Редактировать профиль»).
 *   3. Карточка подписки (`bg-card`, бейдж `#0D7A2E`/`#e8f5e9` для active-тона).
 *   4. Карточка сообщений (бейдж непрочитанных или «Сообщений нет»).
 *   5. Список из 4 действий (вертикально, P-019). «Мои проекты» удалён (PRD 4).
 *   6. Блок «Выйти» (red plate, отдельно, БЕЗ подтверждения).
 *
 * **Design-system:** все карточки `bg-card` (P-038); body/headings `text-ink`
 * (P-039); кнопки вертикально (P-019); токены палитры (P-045) + бейдж `#0D7A2E`
 * exception (P-022/PRD). «Обновить базу» — 5 async-состояний P-021.
 */

export type RefreshState = 'idle' | 'loading' | 'success' | 'error';

export interface CabinetHubViewProps {
  // Профиль (in-memory, может быть null после refresh — P-009)
  profile: AuthProfile | null;
  city: City | null;
  // Подписка (server source of truth, /api/me)
  subscription: AuthSubscription | null;
  subscriptionLoading: boolean;
  daysRemaining: number | null;
  // Сообщения (мок 11.A — по умолчанию 0)
  unreadMessagesCount: number;
  // «Обновить базу» — P-021 async states
  refreshState: RefreshState;
  // Handlers
  onEditProfile: () => void;
  onSubscriptionAction: () => void;
  /** «Продлить заранее −30%» → /cabinet/subscription?promo=AUTORENEW_3D (transient, R3). */
  onRenewEarly: () => void;
  onReferral: () => void;
  onReadMessages: () => void;
  onRefreshDatabase: () => void;
  onLogout: () => void;
}

const PENCIL_ICON = (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

const LOGOUT_ICON = (
  <svg
    width="22"
    height="22"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const REFRESH_LABEL: Record<RefreshState, string> = {
  idle: 'Обновить базу декоров и фурнитуры',
  loading: 'Обновляем базу…',
  success: 'База обновлена',
  error: 'Не удалось обновить. Повторить',
};

function ActionButton({
  label,
  onClick,
  badge,
  busy,
}: {
  label: string;
  onClick: () => void;
  badge?: number;
  busy?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={label}
      aria-busy={busy ? true : undefined}
      className={[
        'w-full min-h-tap rounded-xl bg-card border-[1.5px] border-accent shadow-card',
        'flex items-center gap-3 text-left px-4 py-3 text-ink text-[16px] font-medium',
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
      ].join(' ')}
    >
      <span className="flex-1">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span
          aria-label={`${badge} непрочитанных`}
          className="flex-shrink-0 min-w-6 h-6 px-2 rounded-chip bg-burgundy text-card text-[13px] font-bold inline-flex items-center justify-center"
        >
          {badge}
        </span>
      )}
    </button>
  );
}

export function CabinetHubView(props: CabinetHubViewProps) {
  const initials = props.profile ? getInitials(props.profile.name) : '—';
  const specializationLabel = props.profile
    ? SPECIALIZATION_LABELS_RU[props.profile.specialization]
    : null;

  const sub = props.subscription
    ? getSubscriptionPresentation(props.subscription.status, props.subscription.expiresAt)
    : null;
  const hasUnread = props.unreadMessagesCount > 0;

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Личный кабинет" />

      <section className="flex-1 p-6 overflow-y-auto flex flex-col gap-4">
        {/* 1. VideoButton — first content (P-040) */}
        <VideoButton videoUrl={CABINET_VIDEO_URL} />

        {/* 2. Карточка профиля */}
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4 flex flex-col gap-4">
          <div className="flex items-center gap-4">
            <div
              aria-hidden="true"
              className="flex-shrink-0 w-16 h-16 rounded-full bg-accent text-ink flex items-center justify-center font-display text-h2"
            >
              {initials}
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-display text-h2 text-ink m-0 truncate">
                {props.profile ? props.profile.name : 'Профиль не загружен'}
              </p>
              {props.profile && (
                <p className="text-muted text-[15px] m-0 mt-1 truncate">{props.profile.phone}</p>
              )}
              {(props.city || specializationLabel) && (
                <p className="text-muted text-[15px] m-0 mt-0.5 truncate">
                  {[props.city, specializationLabel].filter(Boolean).join(' · ')}
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={props.onEditProfile}
            aria-label="Редактировать профиль"
            className={[
              'w-full min-h-tap rounded-lg bg-transparent border-[1.5px] border-accent',
              'flex items-center justify-center gap-2 text-ink text-[16px] font-medium',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80',
            ].join(' ')}
          >
            {PENCIL_ICON}
            Редактировать профиль
          </button>
        </div>

        {/* 3. Карточка подписки */}
        {sub ? (
          <div
            className={[
              'rounded-xl shadow-card p-4 flex flex-col gap-1',
              sub.tone === 'active'
                ? 'bg-[#e8f5e9] border-[1.5px] border-[#0D7A2E]'
                : 'bg-card border-[1.5px] border-warning',
            ].join(' ')}
          >
            <div className="flex items-center gap-2">
              <span
                aria-hidden="true"
                className={
                  sub.tone === 'active'
                    ? 'w-2.5 h-2.5 rounded-full bg-[#0D7A2E]'
                    : 'w-2.5 h-2.5 rounded-full bg-warning'
                }
              />
              <span
                className={[
                  'text-[13px] font-bold uppercase tracking-wider',
                  sub.tone === 'active' ? 'text-[#0D7A2E]' : 'text-warning',
                ].join(' ')}
              >
                {sub.badgeText}
              </span>
            </div>
            {sub.dateLabel && (
              <p className="text-ink text-[16px] font-medium m-0 mt-1">{sub.dateLabel}</p>
            )}
            {props.daysRemaining !== null && props.daysRemaining > 0 && (
              <p className="text-muted text-[14px] m-0">Осталось {props.daysRemaining} дн.</p>
            )}
            <button
              type="button"
              onClick={props.onSubscriptionAction}
              aria-label={sub.actionLabel}
              className={[
                'w-full min-h-tap rounded-lg mt-2 bg-cta border-b-2 border-cta-shadow',
                'text-ink text-[16px] font-bold',
                'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
              ].join(' ')}
            >
              {sub.actionLabel}
            </button>
          </div>
        ) : (
          <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4">
            <p className="text-muted text-[15px] m-0" aria-live="polite">
              {props.subscriptionLoading
                ? 'Загружаем статус подписки…'
                : 'Статус подписки недоступен'}
            </p>
          </div>
        )}

        {/* 4. Карточка сообщений */}
        <div className="rounded-xl bg-card border-[1.5px] border-accent shadow-card p-4">
          <p className="text-muted text-[13px] font-bold uppercase tracking-wider m-0">Сообщения</p>
          {hasUnread ? (
            <p className="text-ink text-[16px] m-0 mt-2">
              Непрочитанных сообщений: {props.unreadMessagesCount}
            </p>
          ) : (
            <p className="text-muted text-[16px] m-0 mt-2 text-center">Сообщений нет</p>
          )}
        </div>

        {/* 5. Список действий — 4 кнопки, вертикально (P-019). «Мои проекты» удалён (PRD 4). */}
        <div className="flex flex-col gap-3">
          <ActionButton
            label="Продлить подписку заранее и сэкономь 30%"
            onClick={props.onRenewEarly}
          />
          <ActionButton label="Сэкономь 50% — приведи друга" onClick={props.onReferral} />
          <ActionButton
            label="Прочитать сообщения"
            onClick={props.onReadMessages}
            badge={props.unreadMessagesCount}
          />
          <ActionButton
            label={REFRESH_LABEL[props.refreshState]}
            onClick={props.onRefreshDatabase}
            busy={props.refreshState === 'loading'}
          />
        </div>

        {/* 6. Блок «Выйти» — отдельно ниже списка, red plate, БЕЗ подтверждения */}
        <div className="mt-6">
          <button
            type="button"
            onClick={props.onLogout}
            aria-label="Выйти из профиля"
            className={[
              'w-full min-h-tap rounded-xl bg-card border-[1.5px] border-error shadow-card',
              'flex items-center gap-3 text-left px-4 py-3',
              'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-90',
            ].join(' ')}
          >
            <span className="flex-shrink-0 w-10 h-10 rounded-lg bg-error/10 text-error flex items-center justify-center">
              {LOGOUT_ICON}
            </span>
            <span className="flex-1 text-error text-[16px] font-semibold">Выйти из профиля</span>
          </button>
        </div>
      </section>

      <BottomBar variant="default" />
    </main>
  );
}
